export class DealerAccessAuthorizer {
    /**
     * Provides access checking helper methods for applications on Dealer Access.
     */
    constructor(user, groups, roles) {
        this.user = user;
        this.groups = groups ?? [];
        this.roles = roles ?? [];
    }
    /**
     * Checks if the user has the specified role.
     * @param role - The role name to check.
     * @returns True if the user has the role, false otherwise.
     */
    hasRole(role) {
        return this.user.roles.includes(role);
    }
    /**
     * Checks if the user belongs to the specified group.
     * @param groupID - The group to check, identified by its group ID.
     * @returns True if the user belongs to the group, false otherwise.
     */
    hasGroup(groupID) {
        return this.user.groups.includes(groupID);
    }
    addRole(role) {
        // Normalize to array for easier processing
        const rolesToAdd = Array.isArray(role) ? role : [role];
        // Add each role if it's not already present
        for (const r of rolesToAdd) {
            if (!this.user.roles.includes(r)) {
                this.user.roles.push(r);
            }
        }
    }
    /**
     * Utility method for validating that the supplied roles are valid Dealer Access roles.
     * @param role The role value to validate.
     * @returns true if the role is valid, false otherwise.
     */
    roleIsValid(role) {
        return Object.values(this.roles).includes(role);
    }
    /**
     * Retrieves a property value from the user object.
     * @param property - The property name to retrieve.
     * @returns The value of the specified property.
     */
    get(property) {
        return this.user[property];
    }
    /**
     * Checks if the user is an admin.
     * @returns True if the user is in the admin group, false otherwise.
     */
    isAdmin() {
        return this.user.groups.includes(this.groups.ADMINS);
    }
    /**
     * Checks if the user is a dealer.
     * @returns True if the user is in the dealer group, false otherwise.
     */
    isDealer() {
        return this.user.groups.includes(this.groups.DEALERS);
    }
    /**
     * Checks if the user is a dealer employee.
     * @returns True if the user is in the dealer employee group, false otherwise.
     */
    isDealerEmployeeUser() {
        return this.user.groups.includes(this.groups.DEALER_PEOPLE);
    }
    /**
     * Checks if the user is an internal employee.
     * @returns True if the user is in the people group, false otherwise.
     */
    isGpmUser() {
        return this.user.groups.includes(this.groups.PEOPLE);
    }
    /**
     * Checks if the user is a TM.
     * @returns True if the user is in the people group and has the TM role, false otherwise.
     */
    isTm() {
        return (this.user.groups.includes(this.groups.PEOPLE) &&
            this.user.roles.includes(this.roles.TM));
    }
    /**
     * Checks if the user is a CS user.
     * @returns True if the user is in the people group and has the cs_user role, false otherwise.
     */
    isCsUser() {
        return (this.user.groups.includes(this.groups.PEOPLE) &&
            this.user.roles.includes(this.roles.CS_USER));
    }
}
